/* eslint-disable no-useless-escape */
import jwtDecode from 'jwt-decode';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import durationPlugin from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import currency from 'currency.js';
import { find } from 'lodash';
import env, { SiteId } from '@/env';
import { authHelpers } from '@/services/auth';
import { AuthUser, Currency, Events } from '@/types';
import MainLogoObd from '@/assets/images/main-logo.png';
import MainLogoDakko from '@/assets/images/main-logo-dakko.png';
import faviconOBD from '@/assets/images/logo192-transparent.png';
import faviconDakko from '@/assets/icons/logo192-dakko.png';
import { navigate } from '@reach/router';
import { getAnalytics, logEvent as firebaseLogEvent } from 'firebase/analytics';
import domtoimage from 'dom-to-image';
import { CaptchaInstance } from '@/services/service-directory/captcha';

dayjs.extend(utc);
dayjs.extend(durationPlugin);
dayjs.extend(relativeTime);

export const humanReadableTime = (time: string) => {
  const formatedTime = dayjs(dayjs(time).utc()).local();
  const duration = dayjs.duration(dayjs().diff(formatedTime));
  const seconds = duration.asSeconds();
  const humanizeDuration = dayjs.duration(-seconds, 'seconds').humanize(true);
  return humanizeDuration;
};

export const urlSafe = (text: string) =>
  text
    .trim()
    .replace(/[^a-zA-Z0-9- ]/g, '') // remove invalid characters
    .replace(/\s\s+/g, ' ') // trim whitespace
    .replace(/ /g, '-') // replace space with -
    .toLowerCase();

export const camelCase = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: string, index: number) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
};

export const searchString = (target: string | string[], searchValue: string) => {
  const searchKey = searchValue.toLowerCase();
  const searchTarget = target instanceof Array ? target.map((str) => str.toLowerCase()) : target.toLowerCase();
  const searchResult =
    searchTarget instanceof Array
      ? !!searchTarget.filter((str) => str.includes(searchKey)).length
      : searchTarget.includes(searchKey);
  return searchResult;
};

export const regex = {
  htmlTagsInsensitive: /(<([^>]+)>)/i,
  htmlTagsGlobal: /(<([^>]+)>)/g,
  htmlTagsExcept: (tagName: string) => new RegExp(`\<(?!${tagName}).*?\>`, 'g'),
  lineBreak: /\\n/i,
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
  url: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
  digits: /^\d+$/i,
  phoneNumber: /(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?/i,
};

export const stripHtmlTags = (html: string) => {
  let result = '';
  const div = document.createElement('div');
  div.innerHTML = html;
  result = div.textContent || div.innerText || '';
  div.remove();
  return result;
};

export const saveElementAsImage = (div: HTMLElement, fileName: string, onFinish?: () => void) => {
  domtoimage.toPng(div).then((dataUrl) => {
    const link = document.createElement('a');
    link.download = `${fileName}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (onFinish) onFinish();
  });
};

export const truncateString = (content: string, maxLength: number) => {
  const contentLength = content.length;
  return `${content.slice(0, contentLength > maxLength ? maxLength : contentLength)}${
    contentLength > maxLength ? '...' : ''
  }`;
};

export const truncateStringByWords = (content: string, maxWords: number) => {
  const contentSplited = content?.split(' ');
  if (maxWords >= contentSplited?.length) {
    return content;
  }
  const contentSplitedOptimized = contentSplited?.filter((word, index) => index < maxWords);
  const contentTruncated = contentSplitedOptimized?.join(' ');
  return contentTruncated ? `${contentTruncated}...` : '';
};

export const beautifyLongText = (content: string, maxLength: number) => {
  return truncateString(content.replace(regex.htmlTagsGlobal, '').replace(regex.lineBreak, ''), maxLength);
};

export const lockPageScroll = () => {
  const body = document.getElementById('body');
  body?.classList.add('lock-scroll');
};

export const unlockPageScroll = () => {
  const body = document.getElementById('body');
  body?.classList.remove('lock-scroll');
};

export const getPageQueryParam = (param: string) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
};

export const getAuthUser = (): AuthUser | undefined => {
  try {
    const decodedToken: any = jwtDecode(authHelpers.getIdToken() ?? '');

    const { organisationId: organisation } = decodedToken;
    const organisationId = Array.isArray(organisation) ? organisation[0] : organisation;
    return {
      firstName: decodedToken.given_name,
      lastName: decodedToken.family_name,
      roles: decodedToken.groups,
      organisationId,
    };
  } catch {
    return undefined;
  }
};

export const getSubdomain = () => {
  if (env.dev.subdomain) {
    return env.dev.subdomain;
  }

  const { host } = window.location;
  const hostParts = host.split('.');
  if (hostParts.length < 3) {
    return undefined;
  }

  const subdomain = hostParts[1] ? hostParts[0] : undefined;
  return subdomain;
};

export const currencyFormat = (
  currencies: Currency[],
  amount: number,
  currencyCode: string,
  showSymbol?: boolean,
  precision?: number
) => {
  const defaultFomatter = {
    name: 'United States Dollar',
    code: 'USD',
    symbol: '$',
    decimals: 2,
    displaySymbol: '$',
    displayFormat: '#,###.##',
    displaySymbolFirst: true,
    isoCode: '840',
    displaySpace: 0,
  };
  const matchFomatter = find(currencies, (item: Currency) => item.code === currencyCode);
  const formatter = matchFomatter || defaultFomatter;
  const separator = formatter.displayFormat.charAt(1);
  const decimal = separator === ',' ? '.' : ',';
  let pattern = formatter.displaySymbolFirst ? '! #' : '# !';
  if (formatter.displaySpace === 0) {
    pattern = pattern.replace(' ', '');
  }
  return currency(amount, {
    symbol: showSymbol ?? true ? formatter.displaySymbol : '',
    pattern,
    negativePattern: `-${pattern}`,
    decimal,
    separator,
    precision: precision ?? formatter.decimals,
  }).format();
};

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const removeLastCharaters = (str: string, numberOfCharaters: number) => {
  return str.substring(0, str.length - numberOfCharaters);
};

export const getWebAppInfo = (siteId: string) => {
  const detectWebApp = {
    [SiteId.Obd]: {
      appName: 'OpenBankingDirectory',
      domainName: 'openbankingdirectory',
      tld: '.io',
      domain: env.obdDomain,
      siteName: 'Open Banking Directory',
      company: 'Open Banking Directory',
      mainLogo: MainLogoObd,
      logoPath: 'static/media/main-logo.8268587c.png',
      metadata: {
        site: '@openbankingdirectory',
        favicon: faviconOBD,
      },
      social: {
        facebook: 'https://www.facebook.com/OpenBankingDirectory',
        linkedIn: 'https://www.linkedin.com/company/open-banking-directory',
        twitter: 'https://twitter.com/directory_open',
      },
      email: 'hello@openbankingdirectory.io',
      gdpr: {
        id: 'Cookiebot',
        src: 'https://consent.cookiebot.com/uc.js',
        dataCbid: 'f9edbf86-b231-4cf8-ae8e-2a52b023e8e3',
      },
    },
    [SiteId.DakkoHub]: {
      appName: 'DakkoHub',
      domainName: 'dakkohub',
      tld: '.health',
      domain: env.dakkoHubDomain,
      company: 'Dakko Inc.',
      siteName: 'Dakko Hub',
      mainLogo: MainLogoDakko,
      logoPath: 'static/media/main-logo-dakko.c75cf841.png',
      metadata: {
        site: '@dakkohub',
        favicon: faviconDakko,
      },
      social: {
        facebook: 'https://www.facebook.com/DakkoHealthHub/',
        linkedIn: 'https://www.linkedin.com/company/dakkohealthhub',
        twitter: '',
      },
      email: 'contact@dakko.io',
      gdpr: {
        id: 'Cookiebot',
        src: 'https://consent.cookiebot.com/uc.js',
        dataCbid: 'b7c43e31-75af-4f2b-90d9-e531cf6b2e71',
      },
    },
  };
  return detectWebApp[siteId as keyof typeof detectWebApp];
};

export const TOAST_DURATION = 2000;

export const executeAfterToast = (action: Function) => {
  const executeTimeout = setTimeout(() => {
    action && action();
    clearTimeout(executeTimeout);
  }, TOAST_DURATION);
};

export const persistPageBeforeLogin = () => {
  const { pathname } = window.location;
  navigate('/auth/login', { state: { pathname } });
};

export const logEvent = (event: Events) => {
  const analytics = getAnalytics();
  firebaseLogEvent(analytics, event.toString());
};

export const getRecaptchaToken = () => {
  const { recaptchaEnable } = env.google;
  const windowThis: any = window;
  if (!recaptchaEnable) return undefined;
  return new Promise((resolve, _) => {
    windowThis.grecaptcha.ready(() => {
      const token = windowThis.grecaptcha.execute(env.google.recaptchaKey, {
        action: 'submit',
      });
      resolve(token);
    });
  });
};

const callBackFun = async (response: any) => {
  const windowThis: any = window;

  try {
    const status = await CaptchaInstance.verifyCaptcha(response);
    if (status === 200) {
      const openBankingLayout = document.getElementById('OpenDirectoryLayout');
      const captchaElement = document.getElementById('Captcha');
      authHelpers.clearNeedVerification();
      authHelpers.clearSessionVerification();
      setTimeout(() => {
        captchaElement && captchaElement.removeChild(captchaElement.childNodes[0]);
        openBankingLayout && openBankingLayout.classList.remove('blur');
      }, 1000);
    }
  } catch (error) {
    windowThis.grecaptcha.reset();
  }
};

export const resetRecaptcha = () => {
  const openBankingLayout = document.getElementById('OpenDirectoryLayout');
  const captchaElement = document.getElementById('Captcha');
  const windowThis: any = window;
  const needSessionVerify = authHelpers.getSessionVerification();
  const { recaptchaEnable } = env.google;
  recaptchaEnable &&
    windowThis.grecaptcha.ready(() => {
      if (needSessionVerify === undefined || (needSessionVerify && atob(needSessionVerify) === 'true')) {
        authHelpers.needVerification();
        try {
          windowThis.grecaptcha.reset();
        } catch (error) {}
        captchaElement && captchaElement.classList.remove('hidden');
        openBankingLayout && openBankingLayout.classList.add('blur');
      }
    });
};

export const renderRecaptchaToken = () => {
  try {
    const windowThis: any = window;
    windowThis.grecaptcha.ready(() => {
      windowThis.grecaptcha.render('Captcha', {
        sitekey: '6LeTPcYcAAAAACfp55OcSThH4P69Z-BsaOHVtVX3',
        callback: callBackFun,
      });
    });
  } catch (error) {}
};
